import React, { useMemo, useState } from 'react';
import { EmptyState } from './common/LoadingStates';
import './BacklogBurndownPanel.scss';

/** One day of the over-SLA age profile, as written by the backend into tracker metadata. */
export interface CreationDayBucket {
    day: string;   // YYYY-MM-DD (UTC)
    count: number; // refunds created that day that are currently over SLA
}

interface Props {
    trackerData: {
        metadata?: { generatedAt?: string; overSlaCreationHistogram?: unknown; [key: string]: any };
    } | null;
}

const DAY_MS = 86_400_000;
const AGE_THRESHOLDS_DAYS = [30, 90];

const parseNumber = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Validates the raw metadata list into ascending, non-empty day buckets. */
export const toHistogram = (raw: unknown): CreationDayBucket[] => {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .filter((b: any) => b && typeof b.day === 'string' && !Number.isNaN(Date.parse(b.day)))
        .map((b: any) => ({ day: b.day as string, count: parseNumber(b.count) }))
        .filter((b) => b.count > 0)
        .sort((a, b) => a.day.localeCompare(b.day));
};

/** Whole days between a creation day and the snapshot time. */
const ageDays = (day: string, asOf: number): number =>
    Math.max(0, Math.floor((asOf - Date.parse(day)) / DAY_MS));

const formatDay = (day: string): string =>
    new Date(day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

const BacklogBurndownPanel: React.FC<Props> = ({ trackerData }) => {
    const [hover, setHover] = useState<CreationDayBucket | null>(null);

    const histogram = useMemo(() => toHistogram(trackerData?.metadata?.overSlaCreationHistogram), [trackerData]);

    const stats = useMemo(() => {
        if (histogram.length === 0) {
            return null;
        }
        const asOf = Date.parse(trackerData?.metadata?.generatedAt ?? '') || Date.now();
        const total = histogram.reduce((s, b) => s + b.count, 0);
        // Median age: walk from the newest day (smallest age) until half the backlog is covered.
        let seen = 0;
        let medianAge = 0;
        for (let i = histogram.length - 1; i >= 0; i--) {
            seen += histogram[i].count;
            if (seen * 2 >= total) {
                medianAge = ageDays(histogram[i].day, asOf);
                break;
            }
        }
        const olderThan = AGE_THRESHOLDS_DAYS.map((threshold) => ({
            threshold,
            share: histogram.filter((b) => ageDays(b.day, asOf) > threshold).reduce((s, b) => s + b.count, 0) / total,
        }));
        return { total, medianAge, oldestAge: ageDays(histogram[0].day, asOf), olderThan, asOf };
    }, [histogram, trackerData?.metadata?.generatedAt]);

    // Bar chart: one bar per day that has stuck refunds, oldest on the left, newest on the right.
    const chart = useMemo(() => {
        if (histogram.length === 0) {
            return null;
        }
        const width = 640;
        const height = 140;
        const pad = 8;
        const max = Math.max(1, ...histogram.map((b) => b.count));
        const slot = (width - 2 * pad) / histogram.length;
        const barWidth = Math.max(1, slot - Math.min(2, slot * 0.2));
        const bars = histogram.map((b, i) => {
            const h = (b.count / max) * (height - 2 * pad);
            return { bucket: b, x: pad + i * slot, y: height - pad - h, w: barWidth, h };
        });
        return { width, height, bars, baseline: height - pad };
    }, [histogram]);

    if (!trackerData) {
        return null;
    }

    const tone = stats && stats.olderThan[1].share > 0.25 ? '#a63a50' : stats && stats.olderThan[0].share > 0.25 ? '#b45309' : '#0f766e';

    return (
        <section className="backlog-burndown-panel">
            <div className="backlog-burndown-panel__header">
                <div>
                    <p className="eyebrow">Over-SLA backlog</p>
                    <h2>Stuck backlog age profile</h2>
                    <p className="subtitle">
                        Refunds currently over SLA, by the day they were created. A pile on the right is recent
                        traffic the processor is slow on; a long tail to the left is old backlog nobody is clearing.
                    </p>
                </div>
                {stats && (
                    <div className="backlog-burndown-panel__net" style={{ color: tone }}>
                        {stats.medianAge.toLocaleString()}d
                        <span className="backlog-burndown-panel__net-label">median age</span>
                    </div>
                )}
            </div>

            {!stats && (
                <EmptyState
                    title="No age profile yet"
                    message="The age profile is produced by the data refresh; it appears after the next refresh completes."
                />
            )}

            {stats && chart && (
                <>
                    <div className="backlog-burndown-panel__stats">
                        <div><span className="label">Stuck</span><span className="value">{stats.total.toLocaleString()}</span></div>
                        <div><span className="label">Oldest</span><span className="value">{stats.oldestAge.toLocaleString()}d</span></div>
                        {stats.olderThan.map(({ threshold, share }) => (
                            <div key={threshold}>
                                <span className="label">Older than {threshold}d</span>
                                <span className="value">{Math.round(share * 100)}%</span>
                            </div>
                        ))}
                    </div>
                    <div className="backlog-burndown-panel__chart">
                        <svg
                            className="backlog-burndown-panel__spark"
                            viewBox={`0 0 ${chart.width} ${chart.height}`}
                            preserveAspectRatio="none"
                            role="img"
                            aria-label="Over-SLA refunds by creation day"
                            onMouseLeave={() => setHover(null)}
                        >
                            <line x1="0" x2={chart.width} y1={chart.baseline} y2={chart.baseline} stroke="#dbe4ec" />
                            {chart.bars.map((bar) => (
                                <rect
                                    key={bar.bucket.day}
                                    x={bar.x}
                                    y={bar.y}
                                    width={bar.w}
                                    height={bar.h}
                                    fill={tone}
                                    fillOpacity={hover && hover.day !== bar.bucket.day ? 0.35 : 0.85}
                                    onMouseEnter={() => setHover(bar.bucket)}
                                >
                                    <title>{`${formatDay(bar.bucket.day)}: ${bar.bucket.count.toLocaleString()} stuck`}</title>
                                </rect>
                            ))}
                        </svg>
                        <div className="backlog-burndown-panel__axis">
                            <span>{formatDay(histogram[0].day)}</span>
                            <span className="backlog-burndown-panel__hover" aria-live="polite">
                                {hover ? `${formatDay(hover.day)} · ${hover.count.toLocaleString()} stuck` : 'hover a bar for the day'}
                            </span>
                            <span>{formatDay(histogram[histogram.length - 1].day)}</span>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default BacklogBurndownPanel;
